import { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { GlobalContext } from "../../Context/GlobalContext";

const useAdminCampaigns = ({ Admin }) => {
  const { baseUrl, basicAuth } = useContext(GlobalContext);

  const config = {
    headers: {
      Authorization: basicAuth,
      "Content-Type": "application/json",
    },
  };

  const itemsPerPage = 200;

  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);

  const [logs, setLogs] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);

  const [status, setStatus] = useState("");
  const [campName, setCampName] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [filterText, setFilterText] = useState("");

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [animate, setAnimate] = useState(false);

  const getCampaign = async () => {
    if (!Admin?.pkid || !baseUrl || !basicAuth) return;

    try {
      setLoading(true);

      const raw = JSON.stringify({
        userid: Admin.pkid,
      });

      const requestOptions = {
        method: "POST",
        headers: config.headers,
        body: raw,
        redirect: "follow",
      };

      const res = await fetch(
        `${baseUrl}/SuperAdmin/v1.0/getAllCampaigns`,
        requestOptions
      );

      const data = await res.json();

      if (data?.responseCode === "00") {
        setCampaigns(data?.response || []);
      } else {
        toast.error(data?.responseMessage || "Failed to fetch campaigns");
      }
    } catch (error) {
      console.error("getCampaign error:", error);
      toast.error("Error while fetching campaigns");
    } finally {
      setLoading(false);
    }
  };

  const getLogs = async (logStatus, id, name, userid) => {
    try {
      setModalLoading(true);
      setIsLogsModalOpen(true);
      setStatus(logStatus);
      setCampName(name);
      setLogs([]);

      const raw = JSON.stringify({
        userid,
        campaignId: id,
        status: logStatus,
      });

      const requestOptions = {
        method: "POST",
        headers: config.headers,
        body: raw,
        redirect: "follow",
      };

      const res = await fetch(
        `${baseUrl}/Whatsapp/v1.0/getWhatsappLogs`,
        requestOptions
      );

      const data = await res.json();

      if (data?.responseCode === "00") {
        setLogs(data?.response || []);
      } else {
        toast.error(data?.responseMessage || "Failed to fetch logs");
      }
    } catch (error) {
      console.error("getLogs error:", error);
      toast.error("Error while fetching logs");
    } finally {
      setModalLoading(false);
    }
  };

  const getVendorAccounts = async (userid, phoneNoId) => {
    try {
      const raw = JSON.stringify({
        userid: "A" + userid,
        phoneNumberId: phoneNoId,
      });

      const requestOptions = {
        method: "POST",
        headers: config.headers,
        body: raw,
        redirect: "follow",
      };

      const res = await fetch(
        `${baseUrl}/Whatsapp/v1.0/getTemplates`,
        requestOptions
      );

      const data = await res.json();

      if (data?.responseCode === "00") {
        setTemplates(data?.response?.data || []);
      } else {
        toast.error(data?.responseMessage || "Failed to fetch templates");
      }
    } catch (error) {
      console.error("getVendorAccounts error:", error);
      toast.error("Error while fetching templates");
    }
  };

  const handlePreview = async (templateId, userid, phoneNoId) => {
    setIsPreviewLoading(true);
    setShowPreviewModal(true);

    await getVendorAccounts(userid, phoneNoId);

    setTimeout(() => {
      const template = templates.find((temp) => temp.name === templateId);
      setSelectedTemplate(template || null);
      setIsPreviewLoading(false);
    }, 100);
  };

  useEffect(() => {
    getCampaign();
  }, [Admin?.pkid, baseUrl, basicAuth]);

  useEffect(() => {
    setAnimate(false);
    const timer = setTimeout(() => setAnimate(true), 10);
    return () => clearTimeout(timer);
  }, [templates, selectedTemplate]);

  useEffect(() => {
    if (!showPreviewModal || !selectedTemplate) return;

    setAnimate(false);
    const timer = setTimeout(() => setAnimate(true), 10);
    return () => clearTimeout(timer);
  }, [showPreviewModal, selectedTemplate]);

  const sortedCampaigns = useMemo(() => {
    return [...campaigns].sort(
      (a, b) => new Date(b.indate) - new Date(a.indate)
    );
  }, [campaigns]);

  const totalPages = Math.ceil(sortedCampaigns.length / itemsPerPage);

  const displayedItems = useMemo(() => {
    return sortedCampaigns.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
  }, [sortedCampaigns, currentPage]);

  const filteredItems = useMemo(() => {
    const search = filterText.toLowerCase();

    return displayedItems.filter((item) => {
      return (
        item.id?.toString().toLowerCase().includes(search) ||
        item.user_name?.toLowerCase().includes(search) ||
        item.campaign_name?.toLowerCase().includes(search) ||
        item.template_category?.toLowerCase().includes(search) ||
        item.status?.toLowerCase().includes(search)
      );
    });
  }, [displayedItems, filterText]);

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const closeLogsModal = () => {
    setIsLogsModalOpen(false);
    setLogs([]);
  };

  const closePreviewModal = () => {
    setShowPreviewModal(false);
    setSelectedTemplate(null);
  };

  const refresh = () => {
    getCampaign();
  };

  const replaceVariablesBody = (text = "") => {
    const replacements = ["John Doe", "Messaging"];

    return text.replace(
      /{{(\d+)}}/g,
      (_, index) => replacements[index - 1] || ""
    );
  };

  const downloadExcel = () => {
    if (logs.length === 0) return;

    const headers = [
      [
        "ID",
        "Contact",
        "Business Acc. Id",
        "Phone No. Id",
        "Status",
        "Date",
        "Product",
        "Category",
        "Contact Name",
        "Message Category",
        "Error Code",
        "Error Description",
      ],
    ];

    const logsData = logs.map((item) => [
      item.id,
      item.contact,
      item.business_acc_id,
      item.phone_no_id,
      item.status,
      item.indate,
      item.product,
      item.category,
      item.contact_name || "N/A",
      item.message_category,
      item.error_code || "N/A",
      item.error || "N/A",
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([...headers, ...logsData]);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Logs Report");
    XLSX.writeFile(workbook, `Whatsapp_Logs_${campName}_${status}.xlsx`);
  };

  return {
    loading,
    campaigns,
    filteredItems,

    logs,
    modalLoading,
    isLogsModalOpen,
    closeLogsModal,

    status,
    campName,

    currentPage,
    totalPages,
    itemsPerPage,
    handlePageClick,
    handleNextPage,
    handlePrevPage,

    filterText,
    setFilterText,

    templates,
    selectedTemplate,
    isPreviewLoading,
    showPreviewModal,
    closePreviewModal,
    animate,

    getLogs,
    handlePreview,
    refresh,
    replaceVariablesBody,
    downloadExcel,
  };
};

export default useAdminCampaigns;